import { ProposalDAOInterface } from '../ProposalDAO';
import { ComparisonItemBasedProposalDAO } from '../ProposalDAOImpl';
import * as ProposalNS from '../../Proposal';
import * as ProposalElementNS from '../../ProposalElements';
import { ComparisonItemDAO } from '../ProposalItemDAO';
import { ProposalFieldDAO } from '../ProposalFieldDAO';
import { DataPoints, DataPointResponse } from '../DataPoints';
import { ProposalStyleClassNames } from '../../ProposalStyleClassNames';
import { HoldingCell } from '../../../../views/components/cells';
import DonutData from '@/types/donut-data';
import AllocationDetails from '@/types/allocation-details';

export default class DetailedHoldingsDAO extends ComparisonItemBasedProposalDAO {
  public static buildInstance(proposal: ProposalNS.Proposal, proposalElement: ProposalElementNS.ProposalDataElement): ProposalDAOInterface {
    return new DetailedHoldingsDAO(proposal, proposalElement);
  }

  /* eslint-disable-next-line class-methods-use-this */
  protected getColorKeys(): string[] {
    return ['DEFAULT'];
  }

  protected build(): void {
    let filterCount: number = 10;
    let start: number = 1;
    let extraColumn: boolean = false;

    // test for options
    const daoConfig: ProposalNS.DetailedHoldingsConfig = this.proposalElement.DAO as ProposalNS.DetailedHoldingsConfig;
    if (daoConfig.filterTopXDAOOptions !== undefined) {
      filterCount = daoConfig.filterTopXDAOOptions.count;

      if (daoConfig.filterTopXDAOOptions.start) {
        start = daoConfig.filterTopXDAOOptions.start;
      }
    }

    if (daoConfig.extraColumnsDAOOptions !== undefined && daoConfig.extraColumnsDAOOptions.columns.indexOf('HOLDING_NAME') !== -1) {
      extraColumn = true;
    }

    if (this.customFieldNames != null) {
      for (const name of this.customFieldNames.names) {
        this.addFieldName(this.formatLocalizedString(name), ProposalStyleClassNames.ProposalTableContent);
      }
    }

    let itemDAO: ComparisonItemDAO | null = null;

    /* eslint-disable-next-line no-cond-assign */
    while ((itemDAO = this.nextItem()) !== null) {
      const dpResponse: DataPointResponse<AllocationDetails[]> = DataPoints.getDetailedHoldings(itemDAO.item);
      this.addDisclaimerKeys(dpResponse.disclaimerKeys, itemDAO.item);

      const holdings: AllocationDetails[] = dpResponse.response;

      // As per requirements in CON-8699, Forward General Security holding should be filtered out
      // Since we only have access to the name of the holding, we use it to perform the filtering
      // "Forward General Security" is considered stable and will not change in the future
      const filteredHoldings = holdings.filter(holding => holding.name !== 'Forward General Security');

      let subtotal = 0;
      for (let i = start; i < start + filterCount; i++) {
        const holding = filteredHoldings[i - 1];

        if (holding !== undefined) {
          subtotal += holding.weight;

          if (extraColumn) {
            this.addItemField(itemDAO, holding.name, { weight: holding.weight, name: holding.name });
          } else {
            this.addItemField(itemDAO, holding.name, holding.weight);
          }

          if (this.customFieldNames === null) {
            this.addFieldName(holding.name, ProposalStyleClassNames.ProposalTableContent);
          }
        } else {
          this.addItemField(itemDAO, '', {});

          if (this.customFieldNames === null) {
            this.addFieldName('', ProposalStyleClassNames.ProposalTableContent);
          }
        }
      }

      if (daoConfig.showSubtotal) {
        this.addItemField(itemDAO, '', { weight: subtotal });
        this.addFieldName(this.formatMessage('Proposal.Modules.DetailedHoldings.Subtotal'), ProposalStyleClassNames.ProposalTableContent);
      }
    }
  }

  getDonutData(index: number = 0): DonutData | null {
    const out: DonutData | null = super.getDonutData(index);

    if (out && this.colorsDict) {
      out.colorsDict = this.colorsDict;
    }
    return out;
  }

  // private methods

  private addItemField(itemDAO: ComparisonItemDAO, name: string, value: any): number {
    const field: ProposalFieldDAO = new ProposalFieldDAO(name, value, ProposalStyleClassNames.ProposalTableContent);
    field.cellType = HoldingCell;
    this.addFieldDAO(itemDAO, field);
    return value;
  }
}
